import { type NotificationTargetType } from "@prisma/client";
import type {
  CreateNotificationTriggerInput,
  GetMyNotificationUsersInput,
  GetNotificationTriggerInput,
  ResponseGetNotificationTrigger,
  ResponseGetNotificationTriggerReceiver,
  ResponseGetNotificationTriggers,
  ResponseGetNotificationUserBadgeCount,
  ResponseGetNotificationUsers,
  UpdateNotificationTriggerInput,
} from "src/service/NotificationUserApi/type";
import { serverApi } from "src/service/serverApi";

export const notificationUserApi = serverApi.injectEndpoints({
  endpoints: (builder) => {
    return {
      getMyNotifications: builder.query<
        ResponseGetNotificationUsers,
        GetMyNotificationUsersInput
      >({
        forceRefetch({ currentArg, previousArg }) {
          return (
            currentArg?.cursor !== previousArg?.cursor &&
            currentArg?.cursor !== ""
          );
        },
        merge: (currentCache, newItems, otherArgs) => {
          if (!currentCache.data?.length || otherArgs.arg.cursor === "") {
            currentCache.data = newItems.data;
          } else {
            newItems.data?.forEach((newItem) => {
              if (!currentCache.data.some((item) => newItem.id === item.id)) {
                currentCache.data.push(newItem);
              }
            });
          }

          currentCache.pagination.endCursor = newItems.pagination.endCursor;
          currentCache.pagination.hasNextPage = newItems.pagination.hasNextPage;
        },
        providesTags: ["NotificationUser"],
        query: (params) => {
          return { method: "GET", params, url: "notification_users" };
        },
        serializeQueryArgs: ({ endpointName }) => {
          return endpointName;
        },
      }),
      getNotificationTriggers: builder.query<
        ResponseGetNotificationTriggers,
        GetNotificationTriggerInput
      >({
        providesTags: ["NotificationTriggers"],
        forceRefetch({ currentArg, previousArg }) {
          return (
            currentArg?.cursor !== previousArg?.cursor &&
            currentArg?.cursor !== ""
          );
        },
        merge: (currentCache, newItems, otherArgs) => {
          if (!currentCache.data?.length || otherArgs.arg.cursor === "") {
            currentCache.data = newItems.data;
          } else {
            newItems.data?.forEach((newItem) => {
              if (!currentCache.data.some((item) => newItem.id === item.id)) {
                currentCache.data.push(newItem);
              }
            });
          }

          currentCache.pagination.endCursor = newItems.pagination.endCursor;
          currentCache.pagination.hasNextPage = newItems.pagination.hasNextPage;
        },
        query: (params) => {
          return { method: "GET", params, url: "notification_users/triggers" };
        },
        serializeQueryArgs: ({ endpointName }) => {
          return endpointName;
        },
      }),
      getNotificationBadgeCount: builder.query<
        ResponseGetNotificationUserBadgeCount,
        void
      >({
        providesTags: ["NotificationBadgeCount"],
        query: () => {
          return {
            method: "GET",
            url: `notification_users/badge-count`,
          };
        },
      }),
      resetNotificationBadgeCount: builder.mutation<void, void>({
        invalidatesTags: ["NotificationUser", "NotificationBadgeCount"],
        query: () => {
          return {
            method: "DELETE",
            url: `notification_users/badge-count`,
          };
        },
      }),
      readNotification: builder.mutation<void, string>({
        query: (id) => {
          return {
            method: "PATCH",
            url: `notification_users/${id}/read`,
          };
        },
      }),
      deleteNotification: builder.mutation<void, string>({
        query: (id) => {
          return {
            method: "DELETE",
            url: `notification_users/${id}`,
          };
        },
      }),
      createNotificationTrigger: builder.mutation<
        void,
        CreateNotificationTriggerInput
      >({
        invalidatesTags: ["NotificationTriggers", "NotificationTrigger"],
        query: (body) => {
          return {
            body,
            method: "POST",
            url: "notification_users/triggers",
          };
        },
      }),
      updateNotificationTrigger: builder.mutation<
        ResponseGetNotificationTrigger,
        { body: UpdateNotificationTriggerInput; id: string }
      >({
        invalidatesTags: ["NotificationTriggers", "NotificationTrigger"],
        query: (data) => {
          return {
            body: data.body,
            method: "PATCH",
            url: `notification_users/triggers/${data.id}`,
          };
        },
      }),
      getNotificationTrigger: builder.query<
        ResponseGetNotificationTrigger,
        string
      >({
        providesTags: ["NotificationTrigger"],
        query: (id) => {
          return { method: "GET", url: `notification_users/triggers/${id}` };
        },
        serializeQueryArgs: ({ endpointName }) => {
          return endpointName;
        },
      }),
      getNotificationTriggerReceivers: builder.query<
        ResponseGetNotificationTriggerReceiver[],
        {
          courseId?: string;
          sessionId?: string;
          periodIds?: string;
          gradeNumbers?: string;
          target?: NotificationTargetType;
          notificationTriggerId?: string;
        }
      >({
        query: (params) => {
          return {
            method: "GET",
            params,
            url: `notification_users/triggers/receivers`,
          };
        },
        serializeQueryArgs: ({ endpointName }) => {
          return endpointName;
        },
      }),
      deleteNotificationTrigger: builder.mutation<{ success: boolean }, string>(
        {
          invalidatesTags: ["NotificationTriggers", "NotificationTrigger"],
          query: (id) => {
            return {
              method: "DELETE",
              url: `notification_users/triggers/${id}`,
            };
          },
        },
      ),
    };
  },
  overrideExisting: true,
});

export const {
  useGetMyNotificationsQuery,
  useGetNotificationTriggersQuery,
  useCreateNotificationTriggerMutation,
  useDeleteNotificationMutation,
  useReadNotificationMutation,
  useGetNotificationBadgeCountQuery,
  useResetNotificationBadgeCountMutation,
  useGetNotificationTriggerQuery,
  useUpdateNotificationTriggerMutation,
  useLazyGetNotificationTriggerReceiversQuery,
  useDeleteNotificationTriggerMutation,
} = notificationUserApi;
